import {Alert, Box, Button, Divider, Link} from "@mui/material"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import {useNavigate} from "react-router-dom"

import {Layout} from "@indebted/components/Layout"
import type {DevToolsCreateCustomerResponse} from "@indebted/api"
import {colors} from "@indebted/theme/colors"
import {Text} from "@indebted/theme/Text"

const cardSx = {
	background: colors.primary.white,
	borderRadius: "12px",
	boxShadow: "0px 4px 14px 2px rgba(54,59,100,0.10)",
	width: "100%",
	maxWidth: 600,
	p: 4,
	height: "fit-content",
} as const

const outlinedButtonSx = {
	borderColor: colors.warmBlue["900"],
	color: colors.warmBlue["900"],
	borderRadius: "8px",
	textTransform: "uppercase",
	letterSpacing: "0.06em",
	"&:hover": {background: colors.warmBlue["25"]},
} as const

type CustomerCreatedPageProps = {
	result: DevToolsCreateCustomerResponse
	onReset: () => void
}

function CustomerCreatedPage({result, onReset}: CustomerCreatedPageProps) {
	const navigate = useNavigate()

	return (
		<Layout>
			<Box
				sx={{
					background: colors.gray["100"],
					minHeight: "100%",
					display: "flex",
					justifyContent: "center",
					py: 4,
					px: 2,
				}}
			>
				<Box sx={cardSx}>
					<Text.Large.SemiBold
						color={colors.gray["900"]}
						sx={{
							pb: 2.5,
							borderBottom: `1px solid ${colors.gray["200"]}`,
							mb: 3,
						}}
					>
						Customer Created
					</Text.Large.SemiBold>

					<Alert severity="success" sx={{mb: 3}}>
						Account upload queued — processing may take a moment.
					</Alert>

					<Box display="flex" flexDirection="column" gap={2}>
						<Box>
							<Text.ExtraSmall.SemiBold color={colors.gray["500"]}>Customer ID</Text.ExtraSmall.SemiBold>
							<Text.Small.Regular sx={{fontFamily: "monospace"}} color={colors.gray["900"]}>
								{result.customerID}
							</Text.Small.Regular>
						</Box>

						<Box>
							<Text.ExtraSmall.SemiBold color={colors.gray["500"]}>Secure Code</Text.ExtraSmall.SemiBold>
							<Text.Small.Regular sx={{fontFamily: "monospace"}} color={colors.gray["900"]}>
								{result.secureCode}
							</Text.Small.Regular>
						</Box>

						<Divider />

						<Box display="flex" flexDirection="column" gap={1}>
							<Button
								variant="outlined"
								fullWidth
								endIcon={<OpenInNewIcon fontSize="small" />}
								onClick={() => navigate(`/customers/${result.customerID}`)}
								sx={[Text.Small.Bold.css, outlinedButtonSx]}
							>
								Open in Rocket
							</Button>
							<Link
								href={result.paymentPortalURL}
								target="_blank"
								rel="noopener noreferrer"
								underline="none"
							>
								<Button
									variant="outlined"
									fullWidth
									endIcon={<OpenInNewIcon fontSize="small" />}
									sx={[Text.Small.Bold.css, outlinedButtonSx]}
								>
									Open Payment Portal
								</Button>
							</Link>
						</Box>
					</Box>

					<Button
						variant="text"
						fullWidth
						sx={[
							Text.Small.Bold.css,
							{
								mt: 3,
								color: colors.warmBlue["900"],
								textTransform: "uppercase",
								letterSpacing: "0.08em",
							},
						]}
						onClick={onReset}
					>
						Create another customer
					</Button>
				</Box>
			</Box>
		</Layout>
	)
}

export {CustomerCreatedPage}
